/**
 * Plano no modo ao vivo. Da simulação fica só a definição do plano (fases com as datas e as metas,
 * os 12 furos, as tarefas e as áreas do placar); tudo o que é medida, status ou data sai daqui:
 *
 * - Fase atual pela data real em São Paulo (a simulação vive presa no dia 22/10).
 * - Metas: as que dá para medir saem das vendas reais da fase (do início dela até agora); a meta do
 *   mês (consultorias) sai das consultas Premium pagas no mês corrente. O resto fica sem número
 *   ("Sem dado"), inclusive nas fases que ainda não começaram. Status pela régua do Plano.
 * - Furos: 01, 02, 10 e 12 medidos nos pedidos reais (e na Meta) desde o início da fase atual; o 08
 *   no reembolso dos últimos 28 dias (a janela do Financeiro); os outros dizem "não medido ainda" com o motivo.
 * - Tarefas, status dos furos e placar começam sem registro (a fazer, aberto, sem nota): o que a
 *   equipe muda vai para o servidor (`painelExtras.plano`, junto dos ajustes) e vale para todos; a
 *   simulação só confere a permissão. A revisão de segunda é a da semana real.
 */
import type {
  DateKey,
  Furo,
  KpiValueV2,
  MaturityScore,
  PlanCommand,
  PlanGoal,
  PlanPhase,
  PlanPhaseId,
  PlanReport,
  PlanTask,
} from '@rota/contracts';
import {
  DAY,
  dayStart,
  isPaid,
  isRefundLike,
  noRealKpi,
  type RealCtx,
  type RealModule,
  type RealOrderRow,
  todayKey,
} from './realCore';
import { savedExtras } from './realSettings';

/** Metas da fase medidas nas vendas reais da fase (null = sem venda para medir). */
export const REAL_GOALS: Record<string, (paid: RealOrderRow[]) => number | null> = {
  // Compra de anúncio com o utm_content (nome ou ID do anúncio) visível na venda.
  testPurchaseWithUtm: (paid) =>
    Math.min(1, paid.filter((o) => o.channel === 'meta_ads' && o.utmContent).length),
  // Parte das vendas de anúncio com o clique (_fbc) rastreado até a Kiwify.
  ctaClickCoverage: (paid) => {
    const ads = paid.filter((o) => o.channel === 'meta_ads');
    return ads.length ? ads.filter((o) => o.clickTracked).length / ads.length : null;
  },
  // Compradores do 20X e da consultoria com a origem conhecida (o rótulo da meta cita os dois).
  upsellOriginKnown: (paid) => {
    const up = paid.filter((o) => o.kind === 'upsell' || o.kind === 'premium');
    return up.length ? up.filter((o) => o.channel !== 'sem_origem').length / up.length : null;
  },
  // Take do 20X: vendas pagas do 20X ÷ vendas pagas do curso (o Premium não é o 20X).
  upsellTakeRate: (paid) => {
    const front = paid.filter((o) => o.kind === 'front').length;
    return front ? paid.filter((o) => o.kind === 'upsell').length / front : null;
  },
};

/**
 * Metas do mês corrente em São Paulo (não da janela da fase): consultorias do mês = consultas
 * Premium pagas (US$ 397, consulta individual com o Tiago). Valem até em fase futura (a TV e os
 * Ajustes mostram o mês), mas a fase futura não ganha cor.
 */
export const MONTH_GOALS: Record<string, (paid: RealOrderRow[]) => number> = {
  consultingMonthToDate: (paid) => paid.filter((o) => o.kind === 'premium').length,
};

/** O rótulo da meta do mês diz o que é contado no modo ao vivo. */
const MONTH_LABEL: Record<string, (label: string) => string> = {
  consultingMonthToDate: (l) =>
    l.replace('Consultorias do mês', 'Consultorias do mês (consultas Premium pagas)'),
};

/**
 * Régua do Plano (`goalStatus` de `packages/metrics/src/plan.ts`, copiada: a fronteira 10.2 não
 * deixa o app importar `@rota/metrics`): atingiu = concluída; a partir de 80% da meta = perto; abaixo
 * = longe; sem número = sem dado. Toda meta do plano é "no mínimo" (`max` fica para quando houver).
 */
export function goalStatusOf(
  target: number,
  actual: number | null | undefined,
  direction: 'min' | 'max' = 'min',
): PlanGoal['status'] {
  if (actual == null || !Number.isFinite(actual)) return 'neutral';
  if (direction === 'min') {
    if (actual >= target) return 'done';
    return actual >= 0.8 * target ? 'warn' : 'bad';
  }
  if (actual <= target) return 'done';
  return actual <= 1.2 * target ? 'warn' : 'bad';
}

/** Fase atual (`currentPhaseId` do metrics): a que contém o dia; entre fases, a próxima; depois, a última. */
export function currentPhaseOf(
  phases: readonly Pick<PlanPhase, 'id' | 'from' | 'to'>[],
  today: DateKey,
): PlanPhaseId | null {
  for (const p of phases) if (today >= p.from && today <= p.to) return p.id;
  return phases.find((p) => today < p.from)?.id ?? phases[phases.length - 1]?.id ?? null;
}

const addDaysKey = (key: DateKey, n: number): DateKey =>
  new Date(Date.parse(`${key}T12:00:00Z`) + n * DAY).toISOString().slice(0, 10);

/** Segunda da revisão: hoje, se for segunda; senão, a próxima. */
export function reviewMonday(today: DateKey): DateKey {
  const wd = new Date(`${today}T12:00:00Z`).getUTCDay();
  return addDaysKey(today, (8 - wd) % 7);
}

/** Janela do reembolso do furo 08: a mesma do Financeiro (hoje e os 27 dias antes). */
const REFUND_WINDOW_DAYS = 28;
/** Amostra mínima do reembolso medido (a mesma do Financeiro) e a garantia do curso. */
const MIN_REFUND_SAMPLE = 30;
const GUARANTEE_DAYS = 7;

/** "27/10". */
const dm = (key: DateKey): string => `${key.slice(8, 10)}/${key.slice(5, 7)}`;
const pct = (v: number): string => `${(v * 100).toFixed(1).replace('.', ',').replace(/,0$/, '')}%`;
const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;

/** Placar no modo ao vivo: área sem nota registrada (a tela mostra "sem registro", não um 0). */
export type LiveMaturityScore = MaturityScore & { unrecorded?: true };

/** Contagens do furo medidas nos pedidos reais (o Plano monta as linhas com elas, sem multiplicar). */
export interface LiveFuroCounts {
  /** Início da janela (o da fase atual). */
  since: DateKey;
  adUtm: { n: number; of: number } | null;
  upsellOrigin: { n: number; of: number } | null;
  /** Conversa com o Tiago depois da compra: ainda sem registro real. */
  inConversation: null;
}
/** Furo ao vivo: `period` diz de quando é o número da medida ("desde 27/10", "últimos 7 dias"). */
export type LiveFuro = Furo & { live?: LiveFuroCounts; period?: string };

/** Por que cada furo ainda não tem medida real (texto de tela). */
const NOT_MEASURED: Partial<Record<Furo['id'], string>> = {
  F03: 'as conversas do 20X com o Tiago ainda não são registradas no Painel',
  F04: 'ainda não há registro de quem conversou com o Tiago depois da compra',
  F05: 'o ROAS por criativo com a consultoria precisa do registro das consultorias',
  F06: 'as vendas ainda não são separadas por fatia e formato do criativo',
  F07: 'a chegada ao pitch da VSL ainda não vira evento',
  F09: 'o quiz ainda não manda o código de perfil ao checkout',
  F11: 'o minuto 1 ainda não é separado por lead',
};

type Measure = Pick<Furo, 'seeing'> & {
  actual: string | null;
  status: Furo['measure']['status'];
  period: string;
};

/**
 * O que a equipe registrou no Plano (status das tarefas e dos furos, notas do placar), guardado no
 * servidor junto dos ajustes (kv `painel_ajustes`, `painelExtras.plano`): vale para todos da equipe e
 * volta ao recarregar. A definição do plano (fases, tarefas, prazos) segue a do código.
 */
export interface SavedPlan {
  tasks?: Record<string, { status: PlanTask['status']; evidence?: string }>;
  furos?: Record<string, Furo['status']>;
  maturity?: Record<string, { score: MaturityScore['score']; note?: string; at: DateKey }>;
}

/** Chave do Plano nos registros à parte dos ajustes. */
export const PLAN_KEY = 'plano';

/** Sem os ajustes do servidor não há onde gravar: a tela avisa em vez de fingir que salvou. */
export const PLAN_NOT_SAVED =
  'O Plano não foi salvo: os ajustes do servidor ainda não carregaram. Tente de novo.';

/** O registro com a mudança do comando (sem mexer no resto). */
export function applyPlanCmd(cur: SavedPlan, cmd: PlanCommand, today: DateKey): SavedPlan {
  if (cmd.kind === 'task')
    return {
      ...cur,
      tasks: {
        ...cur.tasks,
        [cmd.id]: { status: cmd.status, ...(cmd.evidence ? { evidence: cmd.evidence } : {}) },
      },
    };
  if (cmd.kind === 'furo') return { ...cur, furos: { ...cur.furos, [cmd.id]: cmd.status } };
  return {
    ...cur,
    maturity: {
      ...cur.maturity,
      [cmd.area]: { score: cmd.score, at: today, ...(cmd.note ? { note: cmd.note } : {}) },
    },
  };
}

export function planModule(ctx: RealCtx): RealModule {
  const { clock, v2 } = ctx;
  /** O registro salvo (lido a cada montagem: outra pessoa pode ter mudado e a abertura relido). */
  const saved = (): SavedPlan => {
    const x = savedExtras(ctx)?.get<SavedPlan>(PLAN_KEY);
    return x && typeof x === 'object' ? x : {};
  };

  /** Medidas reais dos furos desde `since` (a janela da fase atual). */
  async function furoMeasures(since: DateKey, now: number, until: number) {
    const from = Math.min(dayStart(since), now);
    const rows = await ctx.load(from, until);
    const paid = rows.filter(isPaid);
    const today = todayKey(now);
    const [phaseMedia, week, refundRows] = await Promise.all([
      ctx.loadMedia(from, until).catch(() => null),
      ctx.loadMedia(dayStart(today) - 6 * DAY, until).catch(() => null),
      ctx.load(dayStart(today) - (REFUND_WINDOW_DAYS - 1) * DAY, until),
    ]);
    const when = `Desde ${dm(since)}`;
    const period = `desde ${dm(since)}`;
    const front = paid.filter((o) => o.kind === 'front');
    const upsell = paid.filter((o) => o.kind === 'upsell');
    const premium = paid.filter((o) => o.kind === 'premium');

    // Furo 01: utm_content nas vendas do curso pelo link de anúncio; origem do 20X e da consultoria.
    const ads = front.filter((o) => o.channel === 'meta_ads');
    const adsUtm = ads.filter((o) => (o.utmContent ?? '').trim() !== '').length;
    const up = [...upsell, ...premium];
    const upKnown = up.filter((o) => o.channel !== 'sem_origem').length;
    const metaFront = phaseMedia?.meta ? phaseMedia.meta.purchases - (phaseMedia.meta.upsell ?? 0) : null;
    const f01: Measure = {
      period,
      actual: ads.length ? pct(adsUtm / ads.length) : null,
      status: ads.length ? (adsUtm / ads.length >= 0.95 ? 'good' : 'bad') : 'neutral',
      seeing: [
        `${when}: ${adsUtm} de ${plural(ads.length, 'venda', 'vendas')} do curso pelo link de anúncio (UTM) com utm_content`,
        up.length
          ? `origem conhecida de ${upKnown} de ${plural(up.length, 'comprador', 'compradores')} do 20X e da consultoria`
          : 'nenhum comprador do 20X ou da consultoria ainda',
        ...(metaFront != null
          ? [`a Meta atribui ${plural(metaFront, 'compra', 'compras')} do curso no mesmo período`]
          : []),
      ].join('; '),
    };
    const counts: LiveFuroCounts = {
      since,
      adUtm: ads.length ? { n: adsUtm, of: ads.length } : null,
      upsellOrigin: up.length ? { n: upKnown, of: up.length } : null,
      inConversation: null,
    };

    // Furo 02: take do 20X (a mesma conta da meta da fase).
    const take = front.length ? upsell.length / front.length : null;
    const f02: Measure = {
      period,
      actual: take == null ? null : pct(take),
      status: take == null ? 'neutral' : take >= 0.15 ? 'good' : take >= 0.1 ? 'warn' : 'bad',
      seeing: front.length
        ? `${when}: ${upsell.length} de ${plural(front.length, 'comprador', 'compradores')} do curso levaram o 20X; consultorias que vieram do 20X: não medido`
        : `${when}: nenhuma venda do curso ainda`,
    };

    // Furo 08: reembolso do curso e do 20X na mesma janela do Financeiro (vendas dos últimos 28 dias,
    // pela data da venda). Só ganha cor com amostra e com venda que já passou da garantia de 7 dias:
    // venda de ontem ainda não teve tempo de voltar, e o 0% dela não diz que a meta foi batida.
    const sold28 = refundRows.filter(
      (o) => (o.kind === 'front' || o.kind === 'upsell') && (isPaid(o) || isRefundLike(o.status)),
    );
    const lost = sold28.filter((o) => isRefundLike(o.status)).length;
    const sold = sold28.length;
    const matured = sold28.filter((o) => o.at <= now - GUARANTEE_DAYS * DAY).length;
    const f08Why =
      matured === 0
        ? '; vendas ainda dentro da garantia de 7 dias'
        : sold < MIN_REFUND_SAMPLE
          ? `; poucas vendas para medir (menos de ${MIN_REFUND_SAMPLE})`
          : '';
    const f08: Measure = {
      period: 'últimos 28 dias',
      actual: sold ? plural(lost, 'reembolso', 'reembolsos') : null,
      status: sold && !f08Why ? (lost / sold < 0.09 ? 'good' : 'bad') : 'neutral',
      seeing: sold
        ? `Últimos 28 dias: ${plural(lost, 'reembolso', 'reembolsos')} em ${plural(sold, 'venda', 'vendas')} do curso e do 20X (${pct(lost / sold)})${f08Why}`
        : 'Últimos 28 dias: nenhuma venda do curso ou do 20X ainda',
    };

    // Furo 10: ROAS do funil em 7 dias = valor das compras que a Meta atribui ÷ valor gasto na Meta.
    const roas =
      week?.meta && week.totals.spendCents > 0 ? week.meta.valueCents / week.totals.spendCents : null;
    const brl = (c: number) =>
      (c / 100).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL', maximumFractionDigits: 0 });
    const f10: Measure = {
      period: 'últimos 7 dias',
      actual: roas == null ? null : roas.toFixed(2).replace('.', ','),
      status: roas == null ? 'neutral' : roas >= 2 ? 'good' : 'bad',
      seeing:
        roas != null && week?.meta
          ? `Últimos 7 dias: a Meta atribui ${brl(week.meta.valueCents)} em compras para ${brl(week.totals.spendCents)} gastos; vencedores por remessa: não medido`
          : 'Não medido ainda: sem as compras da Meta dos últimos 7 dias',
    };

    // Furo 12: consultas Premium pagas a cada 100 compradores do curso.
    const per100 = front.length ? (premium.length / front.length) * 100 : null;
    const f12: Measure = {
      period,
      actual: per100 == null ? null : `${per100.toFixed(1).replace('.', ',')} a cada 100`,
      status: 'neutral',
      seeing: front.length
        ? `${when}: ${plural(premium.length, 'consulta Premium paga', 'consultas Premium pagas')} em ${plural(front.length, 'comprador', 'compradores')} do curso; preço de hoje: R$ 297 no curso e R$ 697 no 20X`
        : `${when}: nenhuma venda do curso ainda`,
    };

    const measured: Partial<Record<Furo['id'], Measure>> = {
      F01: f01,
      F02: f02,
      F08: f08,
      F10: f10,
      F12: f12,
    };
    return { measured, counts };
  }

  async function getPlan(): Promise<PlanReport> {
    const plan = await (v2.getPlan as () => Promise<PlanReport>)();
    const now = clock();
    // Fim das consultas arredondado para o minuto: o Plano, o Hoje e a TV dividem o mesmo cache.
    const until = Math.ceil(now / 60_000) * 60_000;
    const today = todayKey(now);
    const cur = currentPhaseOf(plan.phases, today);
    // Meta do mês: uma consulta só, feita só se alguma fase tem meta do mês.
    let month: Promise<RealOrderRow[]> | null = null;
    const monthPaid = () => {
      month ??= ctx.load(dayStart(`${today.slice(0, 8)}01`), until).then((r) => r.filter(isPaid));
      return month;
    };
    const phases = await Promise.all(
      plan.phases.map(async (ph): Promise<PlanPhase> => {
        const from = dayStart(ph.from);
        const started = from <= now;
        const needs = started && ph.goals.some((g) => g.kpi && g.kpi in REAL_GOALS);
        const paid = needs
          ? (await ctx.load(from, Math.min(until, dayStart(ph.to) + DAY))).filter(isPaid)
          : [];
        const goals = await Promise.all(
          ph.goals.map(async (g): Promise<PlanGoal> => {
            const kpi = g.kpi ?? '';
            let actual: number | null = null;
            if (MONTH_GOALS[kpi]) actual = MONTH_GOALS[kpi](await monthPaid());
            else if (started && REAL_GOALS[kpi]) actual = REAL_GOALS[kpi](paid);
            return {
              ...g,
              label: MONTH_LABEL[kpi]?.(g.label) ?? g.label,
              actual,
              // Fase que ainda não começou não ganha cor (nem com o número do mês).
              status: started ? goalStatusOf(g.target, actual) : 'neutral',
            };
          }),
        );
        return { ...ph, current: ph.id === cur, goals };
      }),
    );

    // Furos medidos desde o início da fase atual (a mesma janela das metas dela).
    const curPhase = phases.find((p) => p.current);
    const since = curPhase && curPhase.from <= today ? curPhase.from : today;
    const { measured, counts } = await furoMeasures(since, now, until);
    const rec = saved();
    const furos: LiveFuro[] = plan.furos.map((f) => {
      const m = measured[f.id];
      const why = NOT_MEASURED[f.id] ?? 'falta a fonte real desta medida';
      return {
        ...f,
        seeing: m?.seeing ?? `Não medido ainda: ${why}`,
        measure: { ...f.measure, actual: m?.actual ?? null, status: m?.status ?? 'neutral' },
        status: rec.furos?.[f.id] ?? 'aberto',
        ...(m ? { period: m.period } : {}),
        ...(f.id === 'F01' ? { live: counts } : {}),
      };
    });

    // Tarefas: a definição do plano (título, prazo, furos); status e responsável só os registrados.
    const tasks: PlanTask[] = plan.tasks.map((t) => {
      const { evidence: _simEvidence, ...rest } = t;
      const e = rec.tasks?.[t.id];
      return {
        ...rest,
        ownerId: null,
        status: e?.status ?? 'todo',
        ...(e?.evidence ? { evidence: e.evidence } : {}),
      };
    });

    // Placar: as 9 áreas, sem nota até alguém registrar; sem histórico inventado.
    const maturity: LiveMaturityScore[] = plan.maturity.map((m) => {
      const e = rec.maturity?.[m.area];
      return e
        ? { area: m.area, score: e.score, at: e.at, ...(e.note ? { note: e.note } : {}) }
        : { area: m.area, score: 0, at: today, unrecorded: true };
    });

    return {
      phases,
      tasks,
      furos,
      maturity,
      maturityHistory: [],
      review: { weekOf: reviewMonday(today), status: 'pending', route: plan.review.route },
    };
  }

  /**
   * Mudança da equipe: a simulação confere a permissão e o id; o registro vai para o servidor (sem
   * gravar, erro: a tela mostra que não salvou) e volta para todos na próxima abertura.
   */
  async function runPlan(cmd: PlanCommand): Promise<PlanReport> {
    if (v2.plan) await v2.plan(cmd);
    const extras = savedExtras(ctx);
    if (!extras) throw Object.assign(new Error(PLAN_NOT_SAVED), { code: 'unavailable' });
    await extras.save(PLAN_KEY, applyPlanCmd(saved(), cmd, todayKey(clock())));
    return getPlan();
  }

  return {
    overrides: v2.getPlan ? { getPlan, ...(v2.plan ? { plan: runPlan } : {}) } : {},
    async patchSnapshot(_q, snap) {
      // Metas da fase com as vendas reais (as mesmas do Plano). Sem o plano real, sem metas (nunca as
      // da simulação).
      if (!snap.phaseGoals || !v2.getPlan) return;
      const plan = await getPlan().catch(() => null);
      const goals = plan?.phases.find((p) => p.current)?.goals;
      if (goals) snap.phaseGoals = goals;
      else delete snap.phaseGoals;
    },
    patchKpisV2(q, out, sim) {
      // Conversão no pitch: sem evento da chegada ao pitch, não há medida real. O número da simulação
      // não passa (se outro módulo calcular de verdade, o valor dele fica).
      const id = 'pitchConversion';
      if (q.ids.includes(id) && out[id] === sim[id])
        out[id] = noRealKpi(id, 'não medida: a chegada ao pitch da VSL ainda não vira evento') as KpiValueV2;
    },
  };
}
